import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import { Marker, Polyline, Popup, useMap, useMapEvents } from 'react-leaflet';

import { clusterIcon, truckIcon } from './icons';
import SignalBadge from './SignalBadge';
import CopierPosition from './CopierPosition';
import { CLUSTER_DISABLE_AT_ZOOM, grouperParGrille } from './clustering';
import { deplacerMarqueur } from './markerMotion';
import { arretInquietant, couleurFamille, familleDuCamion, libelleFamille } from './status';
import {
  TEXTE_CONFLIT,
  TEXTE_SECOURS,
  ageTexte,
  arretDepuisSecondes,
  aUnePosition,
  cleVehicule,
  dateExacteTexte,
  descriptionMarqueur,
  dureeArretTexte,
  estEnMouvement,
  estPositionDeSecours,
  estPositionFraiche,
  libelleSource,
  niveauFiabilite,
  vitesseTexte,
} from './fleetRules';
import { libelle, texteSur, nomLisible } from '../../lib/libelles';

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

/** En dessous de ce zoom, les plaques sont masquées (sauf survol et sélection). */
export const PLAQUES_ZOOM_MIN = 11;

const LIGNE = 'flex justify-between gap-3';
const LIBELLE = 'text-muted-foreground';

/**
 * Fenêtre d'un camion. Elle répond à la première question d'un exploitant devant
 * une position : est-elle à jour, d'où vient-elle, puis-je m'y fier ?
 */
export function TruckPopup({ vehicle: v, now = Date.now() }) {
  const signal = estPositionFraiche(v, now);
  const famille = familleDuCamion(v);
  const age = ageTexte(v, now);
  const exacte = dateExacteTexte(v);
  const source = libelleSource(v.source);
  const fiabilite = niveauFiabilite(v.quality_score);
  const secours = estPositionDeSecours(v);
  const risque = v.business_context?.risk;
  const conseil = risque && risque !== 'ok' && risque !== 'fallback' ? texteSur(v.business_context?.recommended_action, '') : '';
  const aUnTrajet = !!(v.trip_id || v.trip_status);
  const arret = dureeArretTexte(arretDepuisSecondes(v, now));
  const arretLongEnRoute = arretInquietant(v, now);

  return (
    <div className="min-w-[220px] max-w-[260px]">
      <div className="mb-2 border-b pb-2">
        <div className="flex items-center justify-between gap-2">
          <div className="text-base font-bold">{nomLisible(v.truck?.matricule, 'Camion')}</div>
          <SignalBadge actif={signal} />
        </div>
        <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: couleurFamille(famille) }}
            aria-hidden="true"
          />
          {libelleFamille(famille)}
        </div>
      </div>
      <div className="space-y-1 text-sm">
        <div className={LIGNE}>
          <span className={LIBELLE}>{aUnTrajet ? 'Étape :' : 'Trajet :'}</span>
          <span className="text-right font-medium">
            {aUnTrajet ? libelle('tripStatus', v.trip_status, 'Trajet en cours') : 'Aucun trajet'}
          </span>
        </div>
        {aUnTrajet && (
          <div className={LIGNE}>
            <span className={LIBELLE}>Chauffeur :</span>
            <span className="text-right font-medium">
              {texteSur(v.driver?.full_name || v.driver?.name, 'Non précisé')}
            </span>
          </div>
        )}
        {v.driver?.phone && (
          <div className={LIGNE}>
            <span className={LIBELLE}>Téléphone :</span>
            <a href={`tel:${v.driver.phone}`} className="text-info">
              {v.driver.phone}
            </a>
          </div>
        )}
        <div className={LIGNE}>
          <span className={LIBELLE}>Vitesse :</span>
          <span className="font-medium">{vitesseTexte(v)}</span>
        </div>
        {arret && (
          <div className={LIGNE} data-testid="arret-depuis">
            <span className={LIBELLE}>À l’arrêt depuis :</span>
            <span className={arretLongEnRoute ? 'font-semibold text-flame-ink' : 'font-medium'}>{arret}</span>
          </div>
        )}
        {v.fuel_level_percent != null && (
          <div className={LIGNE}>
            <span className={LIBELLE}>Carburant :</span>
            <span className="font-medium">{Math.round(v.fuel_level_percent)} %</span>
          </div>
        )}
        {v.destination && (
          <div className={LIGNE}>
            <span className={LIBELLE}>Destination :</span>
            <span className="text-right font-medium">{texteSur(v.destination)}</span>
          </div>
        )}
        <div className={LIGNE}>
          <span className={LIBELLE}>Dernière position :</span>
          <span className="text-right font-medium" title={exacte || undefined}>
            {age || 'Date inconnue'}
          </span>
        </div>
        {(source || fiabilite || secours || v.tracking_conflict || conseil) && (
          <div className="space-y-1 rounded bg-muted/50 p-2 text-xs text-muted-foreground">
            {source && (
              <div className="flex justify-between gap-2">
                <span>Source :</span>
                <span className="font-medium text-foreground">{source}</span>
              </div>
            )}
            {fiabilite && (
              <div className="flex justify-between gap-2">
                <span>Fiabilité :</span>
                <span
                  className="font-medium text-foreground"
                  title={`${fiabilite.pourcentage} %`}
                >
                  {fiabilite.libelle}
                </span>
              </div>
            )}
            {v.tracking_conflict && <p className="text-flame-ink">{TEXTE_CONFLIT}</p>}
            {secours && <p className="text-flame-ink">{TEXTE_SECOURS}</p>}
            {conseil && <p className="text-flame-ink">{conseil}</p>}
          </div>
        )}
      </div>
      <div className="mt-2 grid grid-cols-2 gap-1.5">
        {v.trip_id && (
          <Link
            to={`/trips/${v.trip_id}`}
            // `!text-white` : la feuille de Leaflet colore tout lien d'une carte en bleu, ce qui
            // rendait le texte invisible sur le fond bleu du bouton.
            className={`block rounded bg-info py-1 text-center text-sm !text-white hover:bg-info ${v.truck_id ? '' : 'col-span-2'}`}
          >
            Voir le trajet
          </Link>
        )}
        {v.truck_id && (
          <Link
            to={`/trucks/${v.truck_id}`}
            className={`block rounded border border-border bg-card py-1 text-center text-sm !text-foreground hover:bg-muted ${v.trip_id ? '' : 'col-span-2'}`}
          >
            Fiche camion
          </Link>
        )}
        <CopierPosition vehicule={v} className="col-span-2" />
      </div>
    </div>
  );
}

/**
 * Pose le nom accessible d'un marqueur. Leaflet donne `role="button"` et
 * `tabindex="0"` à un marqueur, mais aucun nom : un lecteur d'écran lirait
 * « bouton ». Posé à chaque changement, car `setIcon` refait le nœud.
 */
function useNomAccessible(markerRef, texte, dependance) {
  useEffect(() => {
    const element = markerRef.current?.getElement?.();
    if (!element) return;
    element.setAttribute('aria-label', texte);
    element.setAttribute('title', texte);
  }, [markerRef, texte, dependance]);
}

function TruckMarker({ vehicle, now, selected, onSelect, onDeselect }) {
  const cle = cleVehicule(vehicle);
  const signal = estPositionFraiche(vehicle, now);
  const mouvement = estEnMouvement(vehicle, now);
  const famille = familleDuCamion(vehicle);
  const plaque = vehicle.truck?.matricule;
  const description = descriptionMarqueur(plaque, libelleFamille(famille), signal);
  const nom = [description, ageTexte(vehicle, now)].filter(Boolean).join(', ');

  const icon = truckIcon({
    color: couleurFamille(famille),
    matricule: plaque,
    online: signal,
    heading: vehicle.heading,
    moving: mouvement,
    selected,
    description,
  });

  const markerRef = useRef(null);
  // La position passée à <Marker> reste celle du montage : les suivantes sont
  // appliquées par l'animation, que react-leaflet n'écrase donc pas.
  const positionInitiale = useRef([Number(vehicle.latitude), Number(vehicle.longitude)]);
  const premiereFois = useRef(true);
  const lat = Number(vehicle.latitude);
  const lng = Number(vehicle.longitude);

  useEffect(() => {
    if (premiereFois.current) {
      premiereFois.current = false;
      return undefined;
    }
    const marqueur = markerRef.current;
    if (!marqueur) return undefined;
    return deplacerMarqueur(marqueur, [lat, lng]);
  }, [lat, lng]);

  useNomAccessible(markerRef, nom, icon);

  useEffect(() => {
    const marqueur = markerRef.current;
    if (selected && marqueur && !marqueur.isPopupOpen()) marqueur.openPopup();
  }, [selected]);

  const gestionnaires = useMemo(
    () => ({
      click: () => onSelect && onSelect(cle),
      popupclose: () => onDeselect && onDeselect(cle),
    }),
    [cle, onSelect, onDeselect],
  );

  return (
    <Marker
      ref={markerRef}
      position={positionInitiale.current}
      icon={icon}
      eventHandlers={gestionnaires}
      keyboard
    >
      <Popup>
        <TruckPopup vehicle={vehicle} now={now} />
      </Popup>
    </Marker>
  );
}

function ClusterMarker({ cluster }) {
  const map = useMap();
  const markerRef = useRef(null);
  const nombre = cluster.items.length;
  const description = `${nombre} camions regroupés. Activer pour zoomer.`;
  const icon = clusterIcon({ count: nombre, description });
  useNomAccessible(markerRef, description, icon);

  const gestionnaires = useMemo(
    () => ({
      click: () => {
        const bornes = L.latLngBounds(cluster.items.map((item) => [item.latitude, item.longitude]));
        map.fitBounds(bornes, { padding: [60, 60], maxZoom: CLUSTER_DISABLE_AT_ZOOM + 1 });
      },
    }),
    [cluster, map],
  );

  return (
    <Marker
      ref={markerRef}
      position={[cluster.latitude, cluster.longitude]}
      icon={icon}
      eventHandlers={gestionnaires}
      keyboard
    />
  );
}

/** Truck markers, their clusters, plate labels and recent trails. */
export default function TruckLayer({
  vehicles = EMPTY_ARRAY,
  history = EMPTY_OBJECT, // { truck_id: [[lat,lng], ...] }
  showTrails = true,
  selectedKey = null,
  onSelect,
  now = Date.now(),
  clustering = true,
}) {
  const map = useMap();
  const [zoom, setZoom] = useState(() => map.getZoom());
  useMapEvents({ zoomend: () => setZoom(map.getZoom()) });

  // Les plaques de loin se chevauchent : la feuille de style les masque d'après
  // cette classe, posée sur la carte et non sur chaque marqueur.
  useEffect(() => {
    const conteneur = map.getContainer();
    conteneur.classList.toggle('fleet-plates-hidden', zoom < PLAQUES_ZOOM_MIN);
    return () => conteneur.classList.remove('fleet-plates-hidden');
  }, [map, zoom]);

  const affichables = useMemo(
    () => vehicles.filter((v) => aUnePosition(v) && cleVehicule(v)),
    [vehicles],
  );

  const { singles, clusters } = useMemo(() => {
    const items = affichables.map((v) => ({
      key: cleVehicule(v),
      vehicle: v,
      latitude: Number(v.latitude),
      longitude: Number(v.longitude),
    }));
    if (!clustering) return { singles: items, clusters: [] };
    return grouperParGrille(items, {
      zoom,
      soloKeys: selectedKey ? new Set([selectedKey]) : undefined,
    });
  }, [affichables, clustering, zoom, selectedKey]);

  // Seul le camion sélectionné se désélectionne à la fermeture de sa fenêtre :
  // ouvrir celle d'un autre ferme la précédente, ce n'est pas une désélection.
  const selectedRef = useRef(selectedKey);
  selectedRef.current = selectedKey;
  const surFermeture = useCallback(
    (cle) => {
      if (onSelect && selectedRef.current === cle) onSelect(null);
    },
    [onSelect],
  );

  return (
    <>
      {showTrails &&
        singles.map(({ vehicle: v, key }) => {
          const trail = history[v.truck_id];
          return trail && trail.length > 1 ? (
            <Polyline
              key={`trace:${key}`}
              positions={trail}
              color={couleurFamille(familleDuCamion(v))}
              weight={3}
              opacity={0.6}
            />
          ) : null;
        })}
      {clusters.map((cluster) => (
        <ClusterMarker key={cluster.key} cluster={cluster} />
      ))}
      {singles.map(({ vehicle: v, key }) => (
        <TruckMarker
          key={`camion:${key}`}
          vehicle={v}
          now={now}
          selected={key === selectedKey}
          onSelect={onSelect}
          onDeselect={surFermeture}
        />
      ))}
    </>
  );
}
